/**
 * clubSessionSource — the SEAM between the Club UI and members' swim data.
 *
 * Plan 3 backs it with hand-authored fixtures so the SharedMorningView is
 * verifiable in-app with zero native work. Plan 4 replaces fixtureClubSessionSource()
 * with a bridge-backed source implementing THIS SAME interface (a native N-way
 * method returning members' WorkoutSummary[] + a per-member firehose accessor).
 * The card and view consume ONLY this interface — swapping the impl is the whole
 * of Plan 4's UI change.
 */
import { groupSessions, type MemberSwim } from "./clubSessions";
import type { AttendanceEntry, WorkoutSummary, SwimFirehoseResponse, FirehoseWorkoutEvent, LapLabel } from "@contract/types";

export interface ClubSessionSource {
  /** Every club member's swims (cheap list-level summaries), for groupSessions(). */
  swims: MemberSwim[];
  /** The current member's id — the fact-authority + own-route anchor. */
  viewerId: string;
  /** Human display name for a member id. */
  labelFor: (memberId: string) => string;
  /** A specific swim's fact-authority firehose (for water temp), keyed by the
   *  member AND that swim's uuid — a member swims many mornings, so "the member's
   *  firehose" is ambiguous. null if none. Plan 4's native bridge fetches a
   *  firehose for exactly this (member, workout) pair. */
  firehoseFor: (memberId: string, workoutUUID: string) => SwimFirehoseResponse | null;
  /** Tap-presence entries ("I was there") across all members and days, pre-fetched
   *  like everything else in this source (the seam stays synchronous — D3). memberIds
   *  speak the same id space as viewerId/labelFor: the bridge source maps CloudKit's
   *  `__defaultOwner__` token to its viewer id BEFORE handing entries over. */
  attendance: AttendanceEntry[];
  /** The saved stroke corrections for one swim (stroke-sync 2026-09-12): the
   *  viewer's own from LapLabelStore, a clubmate's from their LapLabels record.
   *  Pre-fetched like the firehoses so the seam stays synchronous. Absent (the
   *  hand-authored fixtures) = no corrections. */
  labelsFor?: (memberId: string, workoutUUID: string) => LapLabel[];
}

const VIEWER_ID = "me";

// Local-time ISO (no 'Z'): clubSessions keys sessions by LOCAL day, matching the app.
function owSummary(uuid: string, startDate: string, meters: number): WorkoutSummary {
  return {
    uuid,
    startDate,
    endDate: startDate,
    activityType: "swimming",
    durationSeconds: 1800,
    totalDistance: { value: meters, unit: "m" },
    swimmingLocationType: "openWater",
  };
}

function poolSummary(uuid: string, startDate: string, meters: number): WorkoutSummary {
  return { ...owSummary(uuid, startDate, meters), swimmingLocationType: "pool" };
}

// HKSwimmingStrokeStyle enum codes (healthKitLabels.ts SWIMMING_STROKE_STYLES) —
// hand-built pool firehose below speaks these directly rather than importing the
// ~650KB captured fixture (bundle-size fix, VP panel round 2026-07-23: commit
// f76b0af's static `import fh11 from "../../fixtures/..."` added ~392KB raw /
// ~25KB gzip to BOTH the web and iOS production bundles, even though this fixture
// module only ever executes off-device — clubSessionSource.ts is statically
// imported by the always-mounted SharedMorningView).
const FREE = 2;
const BACK = 3;
const BREAST = 4;
const FLY = 5;

/** One set's lengths, oldest-to-newest, plus the rest gap BEFORE its first length
 *  (must be >=15s so setGroups.ts's segment-merge doesn't fold it into the
 *  previous set — see setGroups.ts's MERGE_GAP_MS doc). */
interface MiniSet {
  strokes: number[];
  restBeforeSec: number;
}

// Compact, hand-built pool sets: Warmup (4x free) -> The Rockalita (the app's OWN
// signature-set matcher, setMatch.ts's matchRockalita: EXACTLY 8 lengths
// [Fly,Back,Breast,X,X,Breast,Back,Fly] with both middles the same stroke — this
// is a real, load-bearing pattern match, not a hardcoded label) -> Cooldown
// (2x free). 3 sets / 14 lengths total: enough to show labeled sets + stroke-color
// variety at a glance, deliberately far short of a full captured swim's set count
// (VP panel also flagged the old 68-chip/6-row strip as too dense for a glance).
const POOL_SETS: MiniSet[] = [
  { strokes: [FREE, FREE, FREE, FREE], restBeforeSec: 0 },
  { strokes: [FLY, BACK, BREAST, FREE, FREE, BREAST, BACK, FLY], restBeforeSec: 25 },
  { strokes: [FREE, FREE], restBeforeSec: 30 },
];
const POOL_LENGTH_METERS = 25;
// Rough per-length pace by stroke — realism only, no downstream logic reads this
// beyond duration bookkeeping.
const POOL_LAP_SECONDS: Record<number, number> = { [FREE]: 18, [BACK]: 24, [BREAST]: 28, [FLY]: 30 };

/** Builds a small but REAL-pipeline pool firehose: raw `lap` + `segment`
 *  HKWorkoutEvents that setGroups.ts's actual segmentation + setMatch.ts's actual
 *  pattern matcher turn into labeled SetGroups (incl. a genuine "The Rockalita"
 *  match) — not a hand-faked SetGroup[] bypassing the app's own derivation. */
function buildPoolFixtureFirehose(
  uuid: string,
  startDate: string,
  sets: MiniSet[] = POOL_SETS,
  lapSeconds: Record<number, number> = POOL_LAP_SECONDS,
): SwimFirehoseResponse {
  const events: FirehoseWorkoutEvent[] = [];
  let cursorMs = new Date(startDate).getTime();
  let totalMeters = 0;
  for (const set of sets) {
    cursorMs += set.restBeforeSec * 1000;
    const segStartMs = cursorMs;
    for (const stroke of set.strokes) {
      const durationSeconds = lapSeconds[stroke];
      events.push({
        type: "lap",
        date: new Date(cursorMs).toISOString(),
        durationSeconds,
        metadata: { HKSwimmingStrokeStyle: stroke, HKLapLength: `${POOL_LENGTH_METERS} m` },
      });
      cursorMs += durationSeconds * 1000;
      totalMeters += POOL_LENGTH_METERS;
    }
    events.push({
      type: "segment",
      date: new Date(segStartMs).toISOString(),
      durationSeconds: (cursorMs - segStartMs) / 1000,
    });
  }
  const endDate = new Date(cursorMs).toISOString();
  return {
    workout: {
      uuid,
      startDate,
      endDate,
      activityType: "swimming",
      durationSeconds: (cursorMs - new Date(startDate).getTime()) / 1000,
      totalDistance: { value: totalMeters, unit: "m" },
      swimmingLocationType: "pool",
    },
    events,
    activities: [],
    buckets: [],
    route: null,
    metadata: null,
  };
}

/** Total meters across POOL_SETS — kept in sync with the me-pool-0706 SWIMS
 *  entry below so the roster's distance stat and the set strip's derived total
 *  agree (both ultimately trace to the same 14 lengths). */
const POOL_FIXTURE_TOTAL_METERS = POOL_SETS.reduce((sum, s) => sum + s.strokes.length, 0) * POOL_LENGTH_METERS;

function owFirehoseWithTemp(uuid: string, startDate: string, meters: number, degC: number): SwimFirehoseResponse {
  return {
    workout: owSummary(uuid, startDate, meters),
    events: [],
    activities: [],
    buckets: [
      {
        typeName: "HKQuantityTypeIdentifierWaterTemperature",
        category: "quantity",
        unit: "degC",
        sampleCount: 1,
        samples: [{ startDate, endDate: startDate, value: degC }],
      },
    ],
    route: null,
    metadata: null,
  };
}

// Two dawn OW mornings so the date-nav arrows are exercisable in-app, plus one
// earlier pool session so the mixed pool/OW case is representable in fixtures.
const SWIMS: MemberSwim[] = [
  { memberId: "me", swim: owSummary("me-ow-0710", "2026-07-10T06:02:00", 1600) },
  { memberId: "ryan", swim: owSummary("ryan-ow-0710", "2026-07-10T06:05:00", 1800) },
  { memberId: "me", swim: owSummary("me-ow-0708", "2026-07-08T06:04:00", 1500) },
  { memberId: "ryan", swim: owSummary("ryan-ow-0708", "2026-07-08T06:06:00", 1700) },
  { memberId: "me", swim: poolSummary("me-pool-0706", "2026-07-06T17:30:00", POOL_FIXTURE_TOTAL_METERS) },
];

const LABELS: Record<string, string> = { me: "Scott", ryan: "Ryan", dana: "Dana", rachel: "Rachel" };

// One tap-only attendee (no watch, no swim, no stats) on the Jul-10 morning so the
// browser render exercises the stat-less roster row (D3). Day key matches
// localDayKey("2026-07-10T06:02:00") — fixture dates are local-time ISO (no 'Z').
const ATTENDANCE: AttendanceEntry[] = [{ day: "2026-07-10", memberId: "dana" }];

// Keyed by workout UUID (NOT member): only the viewer's JUL-10 swim carries a
// temp signal (22.8°C → 73°F). The Jul-8 swim has no entry, so temp is omitted
// there — exercising both the "viewer's own value" branch and the null branch.
// me-pool-0706 carries a hand-built (compact, real-pipeline) pool firehose so its
// roster row gets an actual condensed set strip (SetGroupRibbon) instead of
// degrading to "no strip".
const FIREHOSES: Record<string, SwimFirehoseResponse> = {
  "me-ow-0710": owFirehoseWithTemp("me-ow-0710", "2026-07-10T06:02:00", 1600, 22.8),
  "me-pool-0706": buildPoolFixtureFirehose("me-pool-0706", "2026-07-06T17:30:00"),
};

export function fixtureClubSessionSource(variant: FixtureClubVariant = "default"): ClubSessionSource {
  if (variant !== "default") return variantSource(variant);
  return {
    swims: SWIMS,
    viewerId: VIEWER_ID,
    labelFor: (id) => LABELS[id] ?? id,
    firehoseFor: (_id, uuid) => FIREHOSES[uuid] ?? null,
    attendance: ATTENDANCE,
  };
}

// ---------------------------------------------------------------------------
// Item 2a front-door variants (`?clubState=…`, clubDevState.ts). Each is the
// club-data half of one journey screen; the status/companion half lives in
// clubDevState.ts. Hand-built through the SAME real pipeline as the default
// fixture (raw lap + segment events → setGroups → setMatch), never a fixture
// import (bundle-size rule above).
// ---------------------------------------------------------------------------

import { CALENDAR_CLUB_SELF, calendarClubLabel, calendarClubMemberSwims } from "./club/fixtures/calendarClub";

export type FixtureClubVariant = "default" | "leaderWaiting" | "memberNoSharedSwim" | "sharedMorning" | "sharedMorningThree" | "calendarClub";

// A fuller pool morning for the shared-morning screen: Warmup → The Rockalita →
// a mixed set → Cooldown. Both swimmers swim the SAME set shapes so every set
// aligns; Ryan's per-stroke pace differs (faster freestyle, slower on the rest)
// so the speed rows show all three results, and his watch logs one length of
// the mixed set as a different stroke so "Where the watches disagreed" has a
// line. His rest before each set absorbs the drift so, like two swimmers
// sharing a lane, each set leaves the wall together (15s apart at most).
const SHARED_SETS: MiniSet[] = [
  { strokes: [FREE, FREE, FREE, FREE], restBeforeSec: 0 },
  { strokes: [FLY, BACK, BREAST, FREE, FREE, BREAST, BACK, FLY], restBeforeSec: 40 },
  { strokes: [BREAST, BREAST, BACK, BACK, FREE, FREE, FREE, FREE], restBeforeSec: 45 },
  { strokes: [FREE, FREE], restBeforeSec: 50 },
];
const RYAN_LAP_SECONDS: Record<number, number> = { [FREE]: 17.5, [BACK]: 25, [BREAST]: 29.5, [FLY]: 30.5 };
// Per-set drift vs Scott (Ryan − Scott, seconds): −2, +5, −4.5 → rest adjusts.
const SHARED_SETS_RYAN: MiniSet[] = [
  { strokes: [FREE, FREE, FREE, FREE], restBeforeSec: 0 },
  { strokes: [FLY, BACK, BREAST, FREE, FREE, BREAST, BACK, FLY], restBeforeSec: 42 },
  { strokes: [BREAST, BREAST, BACK, FREE, FREE, FREE, FREE, FREE], restBeforeSec: 40 },
  { strokes: [FREE, FREE], restBeforeSec: 54.5 },
];
const SHARED_DAY_ME = "2026-07-12T07:02:00";
const SHARED_DAY_RYAN = "2026-07-12T07:02:15";
const SHARED_FH_ME = buildPoolFixtureFirehose("me-pool-0712", SHARED_DAY_ME, SHARED_SETS);
const SHARED_FH_RYAN = buildPoolFixtureFirehose("ryan-pool-0712", SHARED_DAY_RYAN, SHARED_SETS_RYAN, RYAN_LAP_SECONDS);
// A third swimmer (sharedMorningThree): same sets as Scott, a touch slower everywhere.
const RACHEL_LAP_SECONDS: Record<number, number> = { [FREE]: 19, [BACK]: 25, [BREAST]: 29, [FLY]: 31 };
const SHARED_FH_RACHEL = buildPoolFixtureFirehose("rachel-pool-0712", "2026-07-12T07:02:30", SHARED_SETS, RACHEL_LAP_SECONDS);

// The fixture's self id must be this source's viewer id.
if (CALENDAR_CLUB_SELF !== VIEWER_ID) throw new Error("calendarClub fixture self must be the viewer id");

function summaryOf(fh: SwimFirehoseResponse): WorkoutSummary {
  return fh.workout;
}

function variantSource(variant: Exclude<FixtureClubVariant, "default">): ClubSessionSource {
  switch (variant) {
    case "calendarClub":
    {
      // The Club calendar's month (club/fixtures/calendarClub.ts). Every swim gets
      // a hand-built pool firehose, built on first use, so a picked shared day
      // renders its set compare instead of "Only you swam". Same trio as the
      // sharedMorning fixture (Scott's pace; Ryan's sets + pace 15 s later;
      // Rachel's pace 30 s later), cycled by start order within the session and
      // anchored on the session's first start, so the sets line up. 550 m each
      // in the JSON = the 22 lengths of SHARED_SETS.
      const swims = calendarClubMemberSwims();
      const sessions = groupSessions(swims);
      const built = new Map<string, SwimFirehoseResponse>();
      const TRIO: [MiniSet[], Record<number, number>][] = [
        [SHARED_SETS, POOL_LAP_SECONDS],
        [SHARED_SETS_RYAN, RYAN_LAP_SECONDS],
        [SHARED_SETS, RACHEL_LAP_SECONDS],
      ];
      return {
        swims,
        viewerId: VIEWER_ID,
        labelFor: calendarClubLabel,
        firehoseFor: (_id, uuid) => {
          const hit = built.get(uuid);
          if (hit) return hit;
          const session = sessions.find((s) => s.entries.some((e) => e.swim.uuid === uuid));
          if (!session) return null;
          const rank = session.entries.findIndex((e) => e.swim.uuid === uuid) % TRIO.length;
          const [sets, pace] = TRIO[rank];
          const start = new Date(Date.parse(session.startDate) + rank * 15_000).toISOString();
          const fh = buildPoolFixtureFirehose(uuid, start, sets, pace);
          built.set(uuid, fh);
          return fh;
        },
        attendance: [],
      };
    }
    case "leaderWaiting":
      // Only the viewer has swum: the pager shows their own mornings, no clubmate rows.
      return {
        swims: SWIMS.filter((s) => s.memberId === VIEWER_ID),
        viewerId: VIEWER_ID,
        labelFor: (id) => LABELS[id] ?? id,
        firehoseFor: (_id, uuid) => FIREHOSES[uuid] ?? null,
        attendance: [],
      };
    case "memberNoSharedSwim": {
      // The viewer (Ryan) just joined and has no swims; the leader (Scott) has three.
      const scottSwims: MemberSwim[] = SWIMS.filter((s) => s.memberId === VIEWER_ID).map((s) => ({
        memberId: "scott",
        swim: { ...s.swim, uuid: s.swim.uuid.replace(/^me-/, "scott-") },
      }));
      const scottFirehoses: Record<string, SwimFirehoseResponse> = {
        "scott-pool-0706": { ...FIREHOSES["me-pool-0706"], workout: { ...FIREHOSES["me-pool-0706"].workout, uuid: "scott-pool-0706" } },
      };
      return {
        swims: scottSwims,
        viewerId: VIEWER_ID,
        labelFor: (id) => (id === VIEWER_ID ? "Ryan" : id === "scott" ? "Scott" : id),
        firehoseFor: (_id, uuid) => scottFirehoses[uuid] ?? null,
        attendance: [],
      };
    }
    case "sharedMorning":
    case "sharedMorningThree": {
      const shared: Record<string, SwimFirehoseResponse> = {
        "me-pool-0712": SHARED_FH_ME,
        "ryan-pool-0712": SHARED_FH_RYAN,
        ...(variant === "sharedMorningThree" ? { "rachel-pool-0712": SHARED_FH_RACHEL } : {}),
      };
      return {
        swims: [
          { memberId: VIEWER_ID, swim: summaryOf(SHARED_FH_ME) },
          { memberId: "ryan", swim: summaryOf(SHARED_FH_RYAN) },
          ...(variant === "sharedMorningThree" ? [{ memberId: "rachel", swim: summaryOf(SHARED_FH_RACHEL) }] : []),
          ...SWIMS,
        ],
        viewerId: VIEWER_ID,
        labelFor: (id) => LABELS[id] ?? id,
        firehoseFor: (_id, uuid) => shared[uuid] ?? FIREHOSES[uuid] ?? null,
        attendance: ATTENDANCE,
      };
    }
  }
}
